import { useEffect, useState } from "react";
import {
	ChevronDown,
	ChevronUp,
	Copy,
	FileText,
	QrCode,
	Receipt,
	Truck,
	Upload,
	X,
} from "lucide-react";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { Tooltip } from "../../../ui/Tooltip";
import { ClosingChecklist } from "./ClosingChecklist";
import { quotationFooter } from "./quotationFooterStyles";
import type { QuotationClosingDeliveryOption } from "./summary/QuotationSummary.types";
import type { ClosingChecklistItem } from "./summary/QuotationSummary.utils";

type QuotationClosingState =
	| "draft"
	| "registered"
	| "pix_generated"
	| "pending_financial_approval";

interface ClosingOrderData {
	orcId: number;
	oitId: number | null;
}

interface PaymentMethodLite {
	id: number;
	descricao: string;
}

interface QuotationClosingStepProps {
	compact?: boolean;
	orderData: ClosingOrderData | null;
	closingState: QuotationClosingState;
	observacaoPedido: string;
	entrega: QuotationClosingDeliveryOption;
	exibirRecibo: boolean;
	precisaNotaFiscal: boolean;
	temTransportadora: boolean;
	comprovanteFiles: File[];
	createError: string | null;
	closingDataError: string | null;
	pixError: string | null;
	paymentMethods: PaymentMethodLite[];
	selectedPaymentMethodId: number | null;
	isLoadingPaymentMethods: boolean;
	paymentMethodsError: string | null;
	paymentMethodHint: string | null;
	isSavingClosingData: boolean;
	isQuickSaving: boolean;
	isUploadingComprovante: boolean;
	isGeneratingPix: boolean;
	isProcessingFinancial: boolean;
	comprovanteStatus: "idle" | "uploading" | "success" | "error";
	comprovanteError: string | null;
	comprovanteUploadedCount: number;
	canGeneratePix: boolean;
	canSaveClosing: boolean;
	hasGeneratedPix: boolean;
	saveClosingHint: string;
	generatePixHint: string;
	canProcessFinancial: boolean;
	canQuickSave: boolean;
	hasSavedClosingData: boolean;
	isPixPaymentMethodSelected: boolean;
	isQuickSaving: boolean;
	closingChecklist: ClosingChecklistItem[];
	closingStatusLabel: string;
	showComprovanteSection: boolean;
	comprovanteRequired: boolean;
	comprovanteSectionTooltip: string;
	onSelectPaymentMethod: (paymentMethodId: number | null) => void;
	onObservacaoPedidoChange: (value: string) => void;
	onEntregaChange: (value: QuotationClosingDeliveryOption) => void;
	onExibirReciboChange: (value: boolean) => void;
	onPrecisaNotaFiscalChange: (value: boolean) => void;
	onTemTransportadoraChange: (value: boolean) => void;
	onComprovanteFilesChange: (files: File[]) => void;
	onRemoveComprovanteFile: (index: number) => void;
	onRetryComprovanteUpload: () => void;
	onSaveClosingData: () => void;
	onGeneratePix: () => void;
	onViewPix: () => void;
	onProcessFinancial: () => void;
	onQuickSave: () => void;
	onCopyFinalMessage: () => void;
	canCopyFinalMessage: boolean;
	quickSaveHint?: string;
}

const DELIVERY_OPTIONS: Array<{
	value: QuotationClosingDeliveryOption;
	label: string;
}> = [
	{ value: "normal", label: "Normal" },
	{ value: "hoje", label: "Entrega hoje" },
	{ value: "amanha", label: "Entrega amanha" },
	{ value: "fds", label: "Entrega segunda-feira" },
	{ value: "urgente", label: "Urgente" },
];

function appendUniqueFiles(existing: File[], incoming: File[]): File[] {
	const merged = [...existing];
	for (const file of incoming) {
		const alreadyExists = merged.some(
			(current) =>
				current.name === file.name &&
				current.size === file.size &&
				current.lastModified === file.lastModified,
		);
		if (!alreadyExists) merged.push(file);
	}
	return merged;
}

function getStatusLabel(closingStatusLabel: string): string {
	return closingStatusLabel;
}

export function QuotationClosingStep({
	compact = false,
	orderData,
	closingState,
	observacaoPedido,
	entrega,
	exibirRecibo,
	precisaNotaFiscal,
	temTransportadora,
	comprovanteFiles,
	createError,
	closingDataError,
	pixError,
	paymentMethods,
	selectedPaymentMethodId,
	isLoadingPaymentMethods,
	paymentMethodsError,
	paymentMethodHint,
	isSavingClosingData,
	isQuickSaving,
	isUploadingComprovante,
	isGeneratingPix,
	isProcessingFinancial,
	comprovanteStatus,
	comprovanteError,
	comprovanteUploadedCount,
	canGeneratePix,
	canSaveClosing,
	hasGeneratedPix,
	saveClosingHint,
	generatePixHint,
	canProcessFinancial,
	canQuickSave,
	hasSavedClosingData,
	isPixPaymentMethodSelected,
	quickSaveHint,
	closingChecklist,
	closingStatusLabel,
	showComprovanteSection,
	comprovanteRequired,
	comprovanteSectionTooltip,
	onSelectPaymentMethod,
	onObservacaoPedidoChange,
	onEntregaChange,
	onExibirReciboChange,
	onPrecisaNotaFiscalChange,
	onTemTransportadoraChange,
	onComprovanteFilesChange,
	onRemoveComprovanteFile,
	onRetryComprovanteUpload,
	onSaveClosingData,
	onGeneratePix,
	onViewPix,
	onProcessFinancial,
	onQuickSave,
	onCopyFinalMessage,
	canCopyFinalMessage,
}: QuotationClosingStepProps) {
	const [isDraggingComprovante, setIsDraggingComprovante] = useState(false);
	const [showObservacoes, setShowObservacoes] = useState(
		() => Boolean(observacaoPedido.trim()),
	);
	const observacaoPreenchida = observacaoPedido.trim().length > 0;

	const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		onComprovanteFilesChange(appendUniqueFiles(comprovanteFiles, files));
		event.target.value = "";
	};

	const handleComprovanteDrop = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		setIsDraggingComprovante(false);
		const droppedFiles = Array.from(event.dataTransfer?.files || []);
		if (!droppedFiles.length) return;
		onComprovanteFilesChange(appendUniqueFiles(comprovanteFiles, droppedFiles));
	};

	// Ctrl+V: cola imagens da área de transferência como comprovante.
	// Ignora paste quando o foco está num campo de texto (não rouba o paste deles).
	useEffect(() => {
		const handleWindowPaste = (event: ClipboardEvent) => {
			const activeElement = document.activeElement;
			const activeTagName = activeElement?.tagName;
			if (
				activeTagName === "INPUT" ||
				activeTagName === "TEXTAREA" ||
				activeTagName === "SELECT" ||
				(activeElement instanceof HTMLElement && activeElement.isContentEditable)
			) {
				return;
			}

			const items = event.clipboardData?.items;
			if (!items?.length) return;

			const pastedImages: File[] = [];
			for (let index = 0; index < items.length; index += 1) {
				const item = items[index];
				if (item.kind !== "file") continue;
				const file = item.getAsFile();
				if (file && file.type.startsWith("image/")) {
					pastedImages.push(file);
				}
			}

			if (!pastedImages.length) return;

			event.preventDefault();
			onComprovanteFilesChange(appendUniqueFiles(comprovanteFiles, pastedImages));
		};

		window.addEventListener("paste", handleWindowPaste);
		return () => window.removeEventListener("paste", handleWindowPaste);
	}, [comprovanteFiles, onComprovanteFilesChange]);

	return (
		<div className="flex-1 min-h-0 flex flex-col">
			<div
				className={`flex-1 min-h-0 overflow-y-auto ${compact ? "p-2 space-y-2" : "p-2 sm:p-3 md:p-4 space-y-3"}`}
			>
				{createError && (
					<div className="rounded-lg border border-red-500/30 bg-red-600/10 px-3 py-2 text-sm text-red-100">
						<div className="font-medium text-red-300">Falha no fechamento</div>
						<div className="text-[12px] text-red-100/90">{createError}</div>
					</div>
				)}

				{/* Cabeçalho compacto: número • item • status */}
				<div className="flex items-center justify-between gap-2 rounded-lg border border-indigo-500/30 bg-indigo-600/10 px-3 py-1.5">
					<div className="flex items-baseline gap-2 min-w-0">
						<span className="text-sm font-bold text-indigo-100">
							#{orderData?.orcId ?? "--"}
						</span>
						{orderData?.oitId ? (
							<span className="text-[11px] text-indigo-200/70 truncate">
								Item #{orderData.oitId}
							</span>
						) : null}
					</div>
					<span className="shrink-0 rounded-full bg-slate-700/50 px-2 py-0.5 text-[11px] font-medium text-slate-100">
						{getStatusLabel(closingStatusLabel)}
					</span>
				</div>

				{closingDataError && (
					<div className="rounded-lg border border-red-500/30 bg-red-600/10 px-3 py-2 text-sm text-red-100">
						<div className="font-medium text-red-300">Falha ao salvar fechamento</div>
						<div className="text-[12px] text-red-100/90">{closingDataError}</div>
					</div>
				)}

				{/* Pagamento + comprovante */}
				<Card>
					<div className="space-y-3">
						<div className="space-y-1">
							<label className="flex items-center gap-1.5 text-xs text-gray-300">
								Forma de pagamento
								{paymentMethodHint && <Tooltip content={paymentMethodHint} />}
							</label>
							<select
								value={selectedPaymentMethodId ?? ""}
								onChange={(event) =>
									onSelectPaymentMethod(
										event.target.value ? Number(event.target.value) : null,
									)
								}
								disabled={isLoadingPaymentMethods}
								className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
							>
								<option value="">
									{isLoadingPaymentMethods
										? "Carregando formas de pagamento..."
										: "Selecione uma forma de pagamento"}
								</option>
								{paymentMethods.map((method) => (
									<option key={method.id} value={method.id}>
										{method.descricao}
									</option>
								))}
							</select>
							{paymentMethodsError && (
								<div className="text-[11px] text-amber-300">{paymentMethodsError}</div>
							)}
						</div>

						{showComprovanteSection && (
						<div className="rounded-lg border border-navy-600 bg-navy-800/60 p-3 space-y-3">
							<div className="flex items-center gap-1.5 text-sm font-medium text-white">
								<Receipt className="h-4 w-4 text-cyan-300" />
								Comprovante
								{comprovanteRequired ? (
									<span className="text-[10px] font-normal text-amber-300">
										(obrigatorio)
									</span>
								) : (
									<span className="text-[10px] font-normal text-gray-400">
										(opcional)
									</span>
								)}
								<Tooltip content={comprovanteSectionTooltip} />
							</div>
							<label
								onDrop={handleComprovanteDrop}
								onDragOver={(event) => {
									event.preventDefault();
									setIsDraggingComprovante(true);
								}}
								onDragLeave={(event) => {
									event.preventDefault();
									setIsDraggingComprovante(false);
								}}
								className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-3 text-sm transition-colors ${
									isDraggingComprovante
										? "border-cyan-300 bg-cyan-500/15 text-cyan-50"
										: "border-cyan-500/40 bg-cyan-500/5 text-cyan-100 hover:bg-cyan-500/10"
								}`}
							>
								<Upload className="h-4 w-4" />
								<span>
									{isDraggingComprovante ? "Solte o comprovante aqui" : "Selecionar comprovante"}
								</span>
								<input
									type="file"
									multiple
									className="hidden"
									onChange={handleFileInput}
								/>
							</label>
							{/* legenda movida para o tooltip do título Comprovante */}
							{comprovanteFiles.length > 0 && (
								<div className="space-y-2">
									{comprovanteFiles.map((file, index) => (
										<div
											key={`${file.name}-${index}`}
											className="flex items-center justify-between gap-2 rounded-lg border border-navy-600/60 bg-navy-900/50 px-3 py-2"
										>
											<div className="min-w-0 flex-1">
												<div className="truncate text-xs text-white font-medium">{file.name}</div>
												<div className="text-[11px] text-gray-400">{Math.max(1, Math.round(file.size / 1024))} KB</div>
											</div>
											<button
												type="button"
												onClick={() => onRemoveComprovanteFile(index)}
												className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20"
												aria-label="Remover arquivo"
											>
												<X className="h-3.5 w-3.5" />
											</button>
										</div>
									))}
								</div>
							)}
							{comprovanteStatus === "success" && comprovanteUploadedCount > 0 && (
								<div className="text-[11px] text-green-300">{comprovanteUploadedCount} arquivo(s) enviados com sucesso.</div>
							)}
							{comprovanteError && (
								<div className="space-y-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
									<div className="text-[11px] text-red-200">{comprovanteError}</div>
									<Button
										variant="secondary"
										size="sm"
										onClick={onRetryComprovanteUpload}
										disabled={isUploadingComprovante || !orderData}
										className="w-full text-xs"
									>
										{isUploadingComprovante ? "Reenviando..." : "Tentar reenviar comprovante"}
									</Button>
								</div>
							)}
						</div>
						)}
					</div>
				</Card>

				<ClosingChecklist items={closingChecklist} />

				{/* Detalhes do pedido: prazo + toggles */}
				<Card title="Detalhes do pedido">
					<div className="space-y-2">
						<div className="space-y-1">
							<label className="text-xs text-gray-300">Prazo de entrega</label>
							<select
								value={entrega}
								onChange={(event) => onEntregaChange(event.target.value as QuotationClosingDeliveryOption)}
								className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
							>
								{DELIVERY_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>

						<div className="flex items-center justify-between rounded-lg border border-navy-600 bg-navy-800 px-3 py-2">
							<div className="text-sm text-white font-medium">Exibir recibo</div>
							<label className="relative inline-flex items-center cursor-pointer">
								<input
									type="checkbox"
									checked={exibirRecibo}
									onChange={(event) => onExibirReciboChange(event.target.checked)}
									className="sr-only peer"
								/>
								<div className="h-5 w-10 rounded-full bg-navy-700 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-cyan-500 peer-checked:after:translate-x-full" />
							</label>
						</div>

						{/* Nota fiscal + Transportadora na mesma linha */}
						<div className="grid grid-cols-2 gap-2">
							<div className="flex items-center justify-between gap-1 rounded-lg border border-navy-600 bg-navy-800 px-2 py-2">
								<div className="flex items-center gap-1.5 min-w-0 text-xs text-white font-medium">
									<FileText className="h-4 w-4 shrink-0 text-cyan-300" />
									<span className="truncate">Nota fiscal</span>
								</div>
								<label className="relative inline-flex shrink-0 items-center cursor-pointer">
									<input
										type="checkbox"
										checked={precisaNotaFiscal}
										onChange={(event) => onPrecisaNotaFiscalChange(event.target.checked)}
										className="sr-only peer"
									/>
									<div className="h-5 w-10 rounded-full bg-navy-700 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-cyan-500 peer-checked:after:translate-x-full" />
								</label>
							</div>

							<div className="flex items-center justify-between gap-1 rounded-lg border border-navy-600 bg-navy-800 px-2 py-2">
								<div className="flex items-center gap-1.5 min-w-0 text-xs text-white font-medium">
									<Truck className="h-4 w-4 shrink-0 text-cyan-300" />
									<span className="truncate">Transportadora</span>
								</div>
								<label className="relative inline-flex shrink-0 items-center cursor-pointer">
									<input
										type="checkbox"
										checked={temTransportadora}
										onChange={(event) => onTemTransportadoraChange(event.target.checked)}
										className="sr-only peer"
									/>
									<div className="h-5 w-10 rounded-full bg-navy-700 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-cyan-500 peer-checked:after:translate-x-full" />
								</label>
							</div>
						</div>
					</div>
				</Card>

				{/* Observação do pedido (financeiro) — recolhida */}
				<div>
					<button
						type="button"
						onClick={() => setShowObservacoes((prev) => !prev)}
						aria-expanded={showObservacoes}
						className="flex w-full items-center justify-between rounded-lg border border-navy-600 bg-navy-800 px-3 py-2 text-sm text-gray-200 transition-colors hover:bg-navy-700/60"
					>
						<span className="flex items-center gap-2">
							<FileText className="h-4 w-4 text-cyan-300" />
							Observação do pedido
							<span className="text-[11px] text-gray-500">(financeiro, opcional)</span>
							{observacaoPreenchida && (
								<span className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-[10px] text-cyan-200">
									preenchida
								</span>
							)}
						</span>
						{showObservacoes ? (
							<ChevronUp className="h-4 w-4 text-gray-400" />
						) : (
							<ChevronDown className="h-4 w-4 text-gray-400" />
						)}
					</button>
					{showObservacoes && (
						<div className="mt-3 space-y-1">
							<textarea
								value={observacaoPedido}
								onChange={(event) => onObservacaoPedidoChange(event.target.value.slice(0, 600))}
								placeholder="Observação geral do pedido (financeiro)"
								className="min-h-[88px] w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
							/>
							<div className="text-[11px] text-gray-500">{observacaoPedido.length}/600</div>
						</div>
					)}
				</div>

				{pixError && (
					<div className="p-2 bg-red-600/10 border border-red-500/30 rounded-lg">
						<div className="text-xs text-red-300 font-medium">Falha no PIX</div>
						<div className="text-[11px] text-red-100">{pixError}</div>
					</div>
				)}
			</div>

			{/* Footer de ações */}
			<div className={quotationFooter.shell(compact)}>
				{!hasSavedClosingData && !canSaveClosing && (
					<div className={quotationFooter.hint}>
						{selectedPaymentMethodId
							? saveClosingHint
							: "Selecione a forma de pagamento para fechar — ou estacione no Gerenciador."}
					</div>
				)}

				<div className={quotationFooter.row2}>
					<Button
						variant="secondary"
						size="sm"
						onClick={onQuickSave}
						disabled={!canQuickSave || isQuickSaving}
						title={
							quickSaveHint ||
							"Salva no Gerenciador com nome [O-]/[I-], sem fechar o pedido"
						}
						className={quotationFooter.outline}
					>
						{isQuickSaving ? "Salvando..." : "Manter orcamento"}
					</Button>

					<Button
						variant="secondary"
						size="sm"
						onClick={onCopyFinalMessage}
						disabled={!canCopyFinalMessage}
						icon={Copy}
						className={quotationFooter.outline}
					>
						Copiar mensagem final
					</Button>

					<Button
						variant="success"
						size="sm"
						onClick={onSaveClosingData}
						disabled={!canSaveClosing}
						title={saveClosingHint}
						className={quotationFooter.confirm}
					>
						{isSavingClosingData
							? "Fechando..."
							: hasSavedClosingData
								? "Fechado"
								: "Fechar pedido"}
					</Button>
				</div>

				{isPixPaymentMethodSelected && (
					<Button
						variant="primary"
						size="sm"
						onClick={hasGeneratedPix ? onViewPix : onGeneratePix}
						disabled={!canGeneratePix || isGeneratingPix}
						title={generatePixHint}
						icon={QrCode}
						className={quotationFooter.auxiliary}
					>
						{isGeneratingPix
							? "Gerando PIX..."
							: hasGeneratedPix
								? "Ver QR Code PIX"
								: "Gerar PIX"}
					</Button>
				)}
			</div>
		</div>
	);
}
